import { Link, useParams } from 'react-router-dom';
import { articles, articleCategories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 px-4 text-center">
          <h1 className="text-2xl font-bold text-foreground-50">文章不存在</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 4);

  const catInfo = articleCategories.find((c) => c.key === article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    url: `https://51heiliaowang.com/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '51黑料网',
    },
    keywords: article.tags.join(', '),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51heiliaowang.com/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://51heiliaowang.com/category/${article.category}`,
      },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: article.categoryName, path: `/category/${article.category}` },
                  { name: article.title },
                ]}
              />
            </div>
          </div>

          {/* Article Header */}
          <article className="w-full px-4 md:px-6 pb-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-accent-500/15 text-accent-400 border border-accent-500/30 hover:bg-accent-500/25 transition-colors whitespace-nowrap"
                >
                  {article.categoryName}
                </Link>
                {article.isHot && (
                  <span className="px-2 py-1 rounded-full text-xs font-bold bg-accent-500 text-foreground-50 whitespace-nowrap">
                    HOT
                  </span>
                )}
                {article.isNew && (
                  <span className="px-2 py-1 rounded-full text-xs font-bold bg-primary-500 text-foreground-50 whitespace-nowrap">
                    NEW
                  </span>
                )}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-50 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-foreground-500 pb-6 border-b border-background-200/50">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line" /> {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" /> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" /> {article.views.toLocaleString()} 阅读
                  </span>
                </div>
              </div>
            </div>
          </article>

          {/* Article Content */}
          <section className="w-full px-4 md:px-6 pb-10">
            <div className="max-w-4xl mx-auto">
              {/* Cover Image */}
              <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div
                className="prose prose-invert max-w-none text-foreground-200 text-sm md:text-base leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="mt-8 pt-6 border-t border-background-200/50">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-400 border border-background-200/50 whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 bg-background-100/50">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/40 hover:border-primary-500/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    >
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-medium text-foreground-100 line-clamp-2 mb-1.5 group-hover:text-primary-500 transition-colors leading-snug">
                          {rel.title}
                        </h3>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span>{rel.date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line text-[10px]" /> {rel.views.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}